import { LoaderIcon } from '@/app/features/icons/Loader'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import React, { useEffect } from 'react'
import CertificateFooter from './CertificateFooter'
import CertificateHeader from './CertificateHeader'
import CertificatesForm from '@/modules/drivers/forms/certificates/CertificatesForm'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import { useCertificateStore } from '@/modules/drivers/stores/certificateStore'
import { useActivity } from '@/modules/drivers/stores/activityStore'

import useDriverService from '@/modules/drivers/services/DriverService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import {
  faCircleCheckRegular,
  faCircleXRegular,
  FaIcon,
} from '@/lib/fa'

const CertificateModal = ({ onClose, companyId, driverId, selectedDates }) => {
  const [loading, setLoading] = React.useState(false)
  const [errorCode, setErrorCode] = React.useState<string | null>(null)
  const [failedDrivers, setFailedDrivers] = React.useState<string[]>([])
  const [generatedCount, setGeneratedCount] = React.useState(1)
  const [progress, setProgress] = React.useState<{
    current: number
    total: number
    name: string
  } | null>(null)
  const [result, setResult] = React.useState<'success' | 'error' | null>(
    null
  )
  const {
    data,
    setData,
    createForSelectedDrivers,
    setCreateForSelectedDrivers,
    downloadAfterGenerate,
    setDownloadAfterGenerate,
  } = useCertificateStore()
  const { sideCheckedIds } = useSideSelectionStore()
  const { basicDrivers } = useDrivers()
  const { generateAttestation, getAttestationDefaults } = useCalendarService()
  const { triggerRefresh } = useActivity()
  const { getDriver, getDriverWorkStartDate } = useDriverService()
  const { tSafe } = useLocalize()
  const [driverName, setDriverName] = React.useState('')
  const fetchDriver = async () => {
    setLoading(true)
    try {
      const [res, defaults, workStartDate] = await Promise.all([
        getDriver({
          driverId: driverId,
          companyId: companyId,
          withPersonalData: false,
        }),
        getAttestationDefaults(companyId),
        getDriverWorkStartDate({ companyId, driverId }),
      ])

      setDriverName(res.fullName)

      // WPF parity (SelectedDates setter): a gap in the selected days
      // switches the backend to the per-day (non-sequential) path instead
      // of covering the whole span.
      const sortedDays = (selectedDates ?? [])
        .map((d) => new Date(d).toISOString().split('T')[0])
        .sort()
      const isSequential = sortedDays.every(
        (day, i) =>
          i === 0 ||
          (new Date(day).getTime() - new Date(sortedDays[i - 1]).getTime()) /
            86400000 ===
            1,
      )

      setData({
        ...data,
        attestationTypeBasedOnDayType: false,
        isSequential,
        
        shouldInsertActivity: true,
        fillInRemainingTimeWithRestActivity: false,
        activityStartTime: '08:00',
        activityEndTime: '16:00',
        startTime: selectedDates?.length
          ? `${[...selectedDates].map((d) => new Date(d).toISOString().split('T')[0]).sort()[0]} 00:00`
          : undefined,
        endTime: selectedDates?.length
          ? `${[...selectedDates]
              .map((d) => new Date(d).toISOString().split('T')[0])
              .sort()
              .at(-1)} 23:59`
          : undefined,
        birthDate: res.birthday?.split('T')[0],
        drivingLicenceNumber: res.drivingLicenceNumber,
        driverId: Number(driverId),
        selectedDates: selectedDates?.map(
          (d) => new Date(d).toISOString().split('T')[0],
        ),
        workStartDate: workStartDate ?? '',
        // WPF parity: last used value, otherwise company data
        ...(defaults && {
          issuer: defaults.issuer ?? null,
          position: defaults.position ?? null,
          createdPlace: defaults.createdPlace ?? null,
          acceptedPlace: defaults.acceptedPlace ?? null,
        }),
      })
      setLoading(false)
    } catch (err) {
      return null
    }
  }

  const driverNameById = (id: number | string) =>
    basicDrivers.find((d) => String(d.id) === String(id))?.fullName ??
    String(id)


  const fetchAttestationDriverFields = async (id: number | string) => {
    if (String(id) === String(driverId)) {
      return {
        birthDate: data?.birthDate,
        drivingLicenceNumber: data?.drivingLicenceNumber,
        workStartDate: data?.workStartDate,
      }
    }

    const [res, workStartDate] = await Promise.all([
      getDriver({
        driverId: id,
        companyId: companyId,
        withPersonalData: false,
      }),
      getDriverWorkStartDate({ companyId, driverId: id }),
    ])

    return {
      birthDate: res?.birthday?.split('T')[0],
      drivingLicenceNumber: res?.drivingLicenceNumber,
      workStartDate,
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (
      !data?.attestationType ||
      data.attestationType < 1 ||
      data.attestationType > 6
    ) {
      console.warn(
        'Generate attestation blocked: attestationType not selected',
        data,
      )
      return
    }


    const targetDriverIds =
      createForSelectedDrivers && sideCheckedIds.length
        ? sideCheckedIds
        : [driverId]

    setErrorCode(null)
    setFailedDrivers([])
    setLoading(true)

    const failed: string[] = []
    let firstErrorCode: string | null = null
    setGeneratedCount(targetDriverIds.length)

    const today = new Date().toISOString().split('T')[0]

    for (const [index, targetId] of targetDriverIds.entries()) {
      setProgress({
        current: index + 1,
        total: targetDriverIds.length,
        name: driverNameById(targetId),
      })

      try {
        const driverFields = await fetchAttestationDriverFields(targetId)

        const payload = {
          ...data,
          ...driverFields,
          driverId: Number(targetId),
          activityStartTime: `${today} ${data?.activityStartTime || '08:00'}`,
          activityEndTime: `${today} ${data?.activityEndTime || '16:00'}`,
          workStartDate: driverFields.workStartDate
            ? driverFields.workStartDate
            : null,
          searchForNoDataHour: String(data?.searchForNoDataHour ?? '').padStart(
            2,
            '0',
          ),
          searchForNoDataMinute: String(
            data?.searchForNoDataMinute ?? '',
          ).padStart(2, '0'),
          searchForRestLongerThanHour: String(
            data?.searchForRestLongerThanHour ?? '',
          ).padStart(2, '0'),
          searchForRestLongerThanMinute: String(
            data?.searchForRestLongerThanMinute ?? '',
          ).padStart(2, '0'),
        }

        await generateAttestation({
          companyId: companyId,
          attestation: payload,
          download: downloadAfterGenerate,
        })
      } catch (err: any) {
        failed.push(driverNameById(targetId))
        firstErrorCode = firstErrorCode ?? err?.code ?? null
        console.error(err)
      }
    }

    setLoading(false)
    setProgress(null)
    if (failed.length < targetDriverIds.length) {
      triggerRefresh()
    }

    if (failed.length === 0) {
      setResult('success')
      return
    }

    setFailedDrivers(failed)
    setErrorCode(firstErrorCode ?? 'ATTESTATION_NOT_GENERATED')
    setResult('error')
  }

  useEffect(() => {
    if (driverId) {
      fetchDriver()
    }
  }, [driverId])

  useEffect(() => {
    setCreateForSelectedDrivers(false)
    setDownloadAfterGenerate(false)
  }, [])

  return (
    <ModalLayout customZIndex={9998}>
      <form
        onSubmit={handleSubmit}
        noValidate
        className="tw-relative tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[780px] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-place-items-center tw-rounded-lg tw-bg-black/30 tw-backdrop-blur-sm">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
              <LoaderIcon className="tw-animate-spin" />
              {progress && progress.total > 1 && (
                <p className="tw-rounded tw-bg-black/60 tw-px-2 tw-py-1 tw-text-sm tw-text-white">
                  {`${progress.current} z ${progress.total} — ${progress.name}`}
                </p>
              )}
            </div>
          </div>
        )}

        <CertificateHeader close={onClose} driverName={driverName} />
        {errorCode && (
          <div
            role="alert"
            className="tw-mx-4 tw-mb-2 tw-rounded-md tw-border tw-border-red-300 tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-800"
          >
            {tSafe(
              `errorCode.${errorCode}`,
              tSafe(
                'errorCode.ATTESTATION_NOT_GENERATED',
                'Could not generate attestation',
              ),
            )}
          </div>
        )}
        <CertificatesForm />
        <CertificateFooter
          loading={loading}
          disabled={
            !data?.attestationType ||
            data.attestationType < 1 ||
            data.attestationType > 6
          }
        />
        {result && (
          <ModalLayout customZIndex={9999}>
            <div
              role="alertdialog"
              className="tw-flex tw-w-full tw-max-w-[420px] tw-flex-col tw-gap-4 tw-rounded-lg tw-bg-white tw-p-6 tw-shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-sm tw-text-gray-800">
                {result === 'success' ? (
                  <FaIcon
                    icon={faCircleCheckRegular}
                    className="tw-h-10 tw-w-10 tw-text-green-500"
                  />
                ) : (
                  <FaIcon
                    icon={faCircleXRegular}
                    className="tw-h-10 tw-w-10 tw-text-red-500"
                  />
                )}
                <p className="tw-text-lg tw-font-medium tw-text-content-base">
                  {result === 'success'
                    ? generatedCount > 1
                      ? `Zaświadczenia zostały wygenerowane (${generatedCount}).`
                      : tSafe(
                          'attestation.generated',
                          'Zaświadczenie zostało wygenerowane.',
                        )
                    : tSafe(
                        `errorCode.${errorCode ?? 'ATTESTATION_NOT_GENERATED'}`,
                        tSafe(
                          'errorCode.ATTESTATION_NOT_GENERATED',
                          'Zaświadczenie nie zostało wygenerowane',
                        ),
                      )}
                </p>
                {result === 'error' && failedDrivers.length > 0 && (
                  <p className="tw-text-center tw-text-sm tw-text-content-subtle">
                    {`Nie udało się dla: ${failedDrivers.join(', ')}`}
                  </p>
                )}
              </div>
              <div className="tw-flex tw-justify-end">
                <button
                  type="button"
                  className="tw-rounded-md tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-text-white hover:tw-bg-blue-700"
                  onClick={() => {
                    if (result === 'success') {
                      onClose()
                    } else {
                      setResult(null)
                    }
                  }}
                >
                  {tSafe('common.ok', 'OK')}
                </button>
              </div>
            </div>
          </ModalLayout>
        )}
      </form>
    </ModalLayout>
  )
}

export default CertificateModal
